import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Pagination,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CodeIcon from "@mui/icons-material/Code";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import api from "../../api/client";
import { formatDate } from "../../utils/formatDate";
import { useSnackbar } from "../../providers/SnackbarProvider";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VideocamIcon from "@mui/icons-material/Videocam";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ShieldIcon from "@mui/icons-material/Shield";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import useDebounce from "../../hooks/useDebounce";
import { TableLoader } from "../../components/TableLoader";
import { Grow } from "@mui/material";

const Assesment = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const cacheRef = useRef({});

  // States
  const [loading, setLoading] = useState(false);
  const [catgoriesList, setCatgoriesList] = useState([]);
  const [search, setSearch] = useState(
    localStorage.getItem("searchvalue") || ""
  );
  const debouncedSearch = useDebounce(search);
  const [currentPage, setCurrentPage] = useState(+localStorage.getItem("rowsPerPage") || 0);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [totalCount, setTotalCount] = useState(0);
  const [visibleCards, setVisibleCards] = useState([]);
  const location = useLocation();
  const { dashboard, testTypeId } = location.state || {};
  const [tabValue, setTabValue] = useState(
    testTypeId !== undefined ? testTypeId : 0
  ); // 0: All, 1: Self, 2: Proctored

  // Card accent palette (used for Self assessments only) — colors shuffle per card.
  const themeColors = [
    { accent: "#7c3aed", soft: "#f5f0ff", border: "#e4d4fb", ink: "#5b21b6" }, // purple
    { accent: "#0ea5e9", soft: "#eef8ff", border: "#c7e8fb", ink: "#075985" }, // sky
    { accent: "#16a34a", soft: "#eefbf1", border: "#c2eecd", ink: "#166534" }, // green
    { accent: "#f59e0b", soft: "#fff8eb", border: "#fae4b3", ink: "#92400e" }, // amber
    { accent: "#f43f5e", soft: "#fff0f2", border: "#fbd2d8", ink: "#9f1239" }, // rose
    { accent: "#0d9488", soft: "#edfbf8", border: "#bdeee5", ink: "#115e59" }, // teal
  ];

  // Single fixed color for ALL proctored cards so they're instantly recognizable.
  const proctoredColor = { accent: "#4f46e5", soft: "#eef2ff", border: "#c7d2fe", ink: "#3730a3" }; // indigo

  // Fetch assessments
  const categoryList = async () => {
    setLoading(true);

    const getTestType = (val) => {
      if (val === 1) return "Self Assessment";
      if (val === 2) return "Proctored Assessment";
      return "";
    };

    try {
      const payload = {
        page: currentPage,
        size: itemsPerPage,
        search_key: debouncedSearch,
        test_type: getTestType(tabValue),
        assessment_type_id: tabValue === 0 ? null : tabValue,
      };
      const res = await api.post("ListAllAssessmentsForStudent", payload);
      const data = res.data.data;

      setCatgoriesList(data || []);
      setTotalCount(res?.data?.count || 0);
    } catch (error) {
      console.log("Error", error);
      showSnackbar("error", "Failed to fetch assessments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    categoryList();
  }, [debouncedSearch, itemsPerPage, currentPage, tabValue]);

  useEffect(() => {
    localStorage.removeItem("fullscreen");
  }, []);

  // Method outside JSX
  const handleStartTest = (item) => {
    const cleanedTime = String(item?.time_limit || "0").replace(/\D/g, "");

    localStorage.setItem("duration", cleanedTime);
    localStorage.setItem("AssessmentId", item.assessment_id);
    localStorage.setItem("level", item?.level);

    navigate("/Test", {
      state: {
        assessment_id: item.assessment_id,
        cameraAction: item?.camera_assigned_status,
        test_type: item?.test_type,
      },
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCurrentPage(0);
    localStorage.setItem("rowsPerPage", 0);
    navigate(location.pathname, {
      state: { ...(location.state || {}), testTypeId: newValue },
      replace: true,
    });
  };

  useEffect(() => {
    if (catgoriesList.length > 0) {
      setVisibleCards([]); // reset
      catgoriesList.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards((prev) => [...prev, index]);
        }, index * 150); // 150ms gap between cards
      });
    }
  }, [catgoriesList]);

  // Build the visible meta list for a card from the API fields.
  const buildMeta = (item) => {
    const mins = Number(String(item?.time_limit ?? "").replace(/\D/g, ""));
    return [
      item?.level && { icon: <SignalCellularAltIcon />, label: "Level", value: item.level },
      item?.question_type && { icon: <MenuBookIcon />, label: "Type", value: item.question_type },
      item?.no_of_questions && { icon: <HelpOutlineIcon />, label: "Questions", value: item.no_of_questions },
      item?.time_limit && { icon: <AccessTimeIcon />, label: "Duration", value: `${mins} ${mins === 1 ? "minute" : "minutes"}` },
      item?.total_marks != null && { icon: <EmojiEventsIcon />, label: "Marks", value: item.total_marks },
      item?.scheduled_date && { icon: <EventAvailableIcon />, label: "Scheduled", value: formatDate(item.scheduled_date) },
      { icon: <VideocamIcon />, label: "Camera", value: item?.camera_assigned_status === "YES" ? "On" : "Off" },
    ].filter(Boolean);
  };

  /* ===================== Unified clean card =====================
     Same layout for both. Self → shuffled palette; Proctored → fixed indigo. */
  const AssessmentCard = (item, palette, isProctored) => {
    // CTA is driven by attempt_status: NOT_YET → Start, IN_PROGRESS → Resume,
    // COMPLETED → Completed/Retake (see quiz contract §1).
    const isCompleted = item?.attempt_status === "COMPLETED";
    const isInProgress = item?.attempt_status === "IN_PROGRESS";
    const isDeprecated = item?.deprecated === "YES";
    const disabled = isDeprecated || (isProctored && isCompleted);
    const ctaLabel = isCompleted ? "Retake" : isInProgress ? "Resume" : "Start Assessment";
    const meta = buildMeta(item);

    return (
      <Box
        onClick={disabled ? undefined : () => handleStartTest(item)}
        sx={{
          position: "relative",
          overflow: "hidden",
          height: "100%",
          minHeight: 248,
          display: "flex",
          flexDirection: "column",
          borderRadius: "16px",
          p: 2.5,
          backgroundColor: "#fff",
          backgroundImage: `radial-gradient(150px 150px at 100% 0%, ${palette.soft} 0%, rgba(255,255,255,0) 70%), radial-gradient(180px 180px at 0% 100%, ${palette.soft} 0%, rgba(255,255,255,0) 70%)`,
          border: `1px solid ${palette.border}`,
          borderTop: `4px solid ${palette.accent}`,
          boxShadow: "0 4px 14px rgba(20,40,80,0.06)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.75 : 1,
          transition: "all .25s ease",
          "&:hover": disabled ? {} : { transform: "translateY(-4px)", boxShadow: "0 16px 32px rgba(20,40,80,0.14)" },
        }}
      >
        {/* Decorative watermark icon (acts as a soft background image) */}
        <Box sx={{ position: "absolute", right: -14, bottom: -14, opacity: 0.06, pointerEvents: "none", transform: "rotate(-10deg)" }}>
          {isProctored ? (
            <ShieldIcon sx={{ fontSize: 150, color: palette.accent }} />
          ) : (
            <CodeIcon sx={{ fontSize: 150, color: palette.accent }} />
          )}
        </Box>

        {/* Top row: icon tile + type tag */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: palette.soft }}>
            {isProctored ? (
              <ShieldIcon sx={{ fontSize: 24, color: palette.accent }} />
            ) : (
              <CodeIcon sx={{ fontSize: 24, color: palette.accent }} />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.4, borderRadius: "999px", backgroundColor: palette.soft, border: `1px solid ${palette.border}` }}>
            {isProctored && <WorkspacePremiumIcon sx={{ fontSize: 13, color: palette.accent }} />}
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: palette.accent, letterSpacing: 0.5 }}>
              {isProctored ? "PROCTORED" : "SELF"}
            </Typography>
          </Box>
        </Box>

        {/* Title + star */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1.25, minWidth: 0 }}>
            {item.category}
          </Typography>
          <StarRoundedIcon sx={{ fontSize: 18, color: palette.accent, flexShrink: 0 }} />
          {isCompleted && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, ml: 0.5, px: 0.85, py: 0.2, borderRadius: "999px", backgroundColor: "#ecfdf3", border: "1px solid #a7f3d0" }}>
              <CheckCircleIcon sx={{ fontSize: 12, color: "#16a34a" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#15803d" }}>DONE</Typography>
            </Box>
          )}
          {isInProgress && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, ml: 0.5, px: 0.85, py: 0.2, borderRadius: "999px", backgroundColor: "#fff7ed", border: "1px solid #fed7aa" }}>
              <AccessTimeIcon sx={{ fontSize: 12, color: "#ea580c" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#c2410c" }}>IN PROGRESS</Typography>
            </Box>
          )}
        </Box>

        {/* Subtitle */}
        {/* <Typography sx={{ fontSize: 12.5, color: "#7b8794", mt: 0.5 }}>
          {item.level || (isProctored ? "Supervised · camera & full-screen monitored" : "Practice freely · no monitoring")}
        </Typography> */}

        {/* Meta lines */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.75, flex: 1, alignContent: "flex-start" }}>
          {meta.map((m, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1, py: 0.5, borderRadius: "8px", backgroundColor: "#f6f8fb", border: "1px solid #eef1f5" }}>
              {React.cloneElement(m.icon, { sx: { fontSize: 15, color: palette.accent } })}
              <Typography sx={{ fontSize: 12, color: "#475569" }}>
                <span style={{ fontWeight: 700, color: "#1e293b" }}>{m.label}: </span>{m.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Action */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, pt: 1.5, borderTop: "1px solid #eef1f5" }}>
          {disabled ? (
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#9aa4b2" }}>
              {isDeprecated ? "Unavailable" : "Completed"}
            </Typography>
          ) : (
            <Button
              size="small"
              variant="contained"
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 2.25,
                backgroundColor: palette.accent,
                boxShadow: `0 6px 14px ${palette.accent}44`,
                "&:hover": { backgroundColor: palette.ink },
              }}
            >
              {ctaLabel}
              <ArrowForwardIosIcon sx={{ fontSize: 13, ml: 0.5 }} />
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, pb: 10, minHeight: "100vh" }}>
      {dashboard === true && (
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "6px",
              cursor: "pointer",
              width: "fit-content",
              transition: "0.2s",
              "&:hover": { color: "#0a73b1" },
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "18px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "inherit" }}
            >
              Back
            </Typography>
          </Grid>
        </Grid>
      )}
      <>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Grid
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Grid
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 0.5,
                alignItems: "center",
              }}
            >
              <Box className="icon-box">
                <AssignmentIcon sx={{ color: "#1976d2", fontSize: "28px" }} />
              </Box>
              {/* Title */}
              <Typography
                sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}
              >
                Assessments
              </Typography>
            </Grid>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search assessments"
              value={search}
              className="custom-search-field"
              onChange={(e) => (
                setSearch(e.target.value),
                localStorage.setItem("searchvalue", e.target.value)
              )}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>
          <Typography sx={{ color: "#707680", fontSize: "16px", mt: "10px" }}>
            Pick your preferred technology field and start your professional
            assessment journey.
          </Typography>
        </Box>

        {/* Filter Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              minHeight: "40px",
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                borderRadius: "20px",
                marginRight: "10px",
                minHeight: "40px",
                padding: "6px 20px",
                color: "#707680",
                backgroundColor: "#f5f5f5",
                transition: "all 0.3s ease",
                "&.Mui-selected": {
                  color: "white",
                  backgroundColor: "#1c97e3",
                  boxShadow: "0px 4px 10px rgba(28, 151, 227, 0.3)",
                },
                "&:hover": {
                  backgroundColor: "#eeeeee",
                  "&.Mui-selected": {
                    backgroundColor: "#1c97e3",
                  },
                },
              },
            }}
          >
            <Tab label="All Assessments" />
            <Tab label="Self Assessments" />
            <Tab label="Proctored Assessments" />
          </Tabs>
        </Box>

        {/* Cards */}
        {loading ? (
          <TableLoader message="Fetching assessments..." />
        ) : catgoriesList?.length !== 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              columnGap: 3,
              rowGap: 8,
            }}
          >
            {catgoriesList.map((item, index) => {
              const isProctored = item?.test_type === "Proctored Assessment";
              // Self → shuffled palette; Proctored → one fixed color.
              const palette = isProctored
                ? proctoredColor
                : themeColors[index % themeColors.length];
              const completedProctored =
                isProctored && item?.attempt_status === "COMPLETED";

              return (
                <Grow
                  in={visibleCards.includes(index)}
                  timeout={500}
                  key={item.assessment_id}
                >
                  <Box sx={{ height: "100%" }}>
                    <Tooltip
                      title={
                        item?.deprecated === "YES"
                          ? "⚠ Writing is disabled"
                          : completedProctored
                            ? "⚠ You have already completed this proctored test"
                            : ""
                      }
                      placement="top"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "primary.main",
                            color: "white",
                            fontSize: 13,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            width: "fit-content",
                            maxWidth: "none",
                            padding: "6px 10px",
                          },
                        },
                        arrow: { sx: { color: "primary.main" } },
                      }}
                    >
                      <Box sx={{ height: "100%" }}>
                        {AssessmentCard(item, palette, isProctored)}
                      </Box>
                    </Tooltip>
                  </Box>
                </Grow>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
              px: 3,
            }}
          >
            <Box
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: "50%",
                p: 4,
                mb: 3,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />
            </Box>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 700,
                color: "#424242",
                mb: 1,
              }}
            >
              No Assessments Available
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                color: "#757575",
                textAlign: "center",
                maxWidth: 500,
                mb: 3,
              }}
            >
              There are no exams assigned to you at the moment. Please check back later or contact your class authorities.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SearchIcon sx={{ fontSize: 20, color: "#9e9e9e" }} />
                <Typography sx={{ fontSize: 14, color: "#9e9e9e" }}>
                  Try adjusting your search
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HelpOutlineIcon sx={{ fontSize: 20, color: "#9e9e9e" }} />
                <Typography sx={{ fontSize: 14, color: "#9e9e9e" }}>
                  Contact support for help
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {loading
          ? null
          : ((currentPage === 0 && catgoriesList?.length > itemsPerPage - 1) ||
            currentPage !== 0) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap",
                gap: 1.5,
                mt: 4,
                pt: 3,
                borderTop: "1px solid #eef1f5",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                disabled={currentPage === 0}
                onClick={() => (
                  setCurrentPage(currentPage - 1),
                  localStorage.setItem("rowsPerPage", currentPage - 1)
                )}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  color: currentPage === 0 ? "#a2a2a2" : "#414651",
                  borderColor: "#d0d0d0",
                }}
              >
                Previous
              </Button>

              <Pagination
                page={currentPage + 1}
                onChange={(e, value) => (
                  setCurrentPage(value - 1),
                  localStorage.setItem("rowsPerPage", value - 1)
                )}
                count={Math.ceil(totalCount / itemsPerPage)}
                siblingCount={2}
                boundaryCount={2}
                shape="rounded"
                hidePrevButton
                hideNextButton
              />

              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                disabled={currentPage + 1 >= Math.ceil(totalCount / itemsPerPage)}
                onClick={() => (
                  setCurrentPage(currentPage + 1),
                  localStorage.setItem("rowsPerPage", currentPage + 1)
                )}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  color:
                    currentPage + 1 >= Math.ceil(totalCount / itemsPerPage)
                      ? "#a2a2a2"
                      : "#414651",
                  borderColor: "#d0d0d0",
                }}
              >
                Next
              </Button>
            </Box>
          )}
      </>
    </Box>
  );
};

export default Assesment;
